import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChefHat, Bell, Barcode as BarcodeIcon, CheckCircle2, Flame, MonitorX } from "lucide-react";
import { printSticker } from "@/lib/print";
import { Barcode } from "@/components/Barcode";

const STATUS = { pending: { label: "Baru", color: "bg-blue-500", next: "cooking" }, cooking: { label: "Dimasak", color: "bg-amber-500", next: "ready" }, ready: { label: "Siap", color: "bg-emerald-500", next: "done" } };

function playBell() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const beep = (t) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination); o.type = "sine"; o.frequency.value = 880;
      g.gain.setValueAtTime(0.001, t); g.gain.exponentialRampToValueAtTime(0.4, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.35); o.start(t); o.stop(t + 0.38);
    };
    const n = ctx.currentTime; beep(n); beep(n + 0.45);
  } catch (e) { /* audio unsupported */ }
}

export default function KDS() {
  const { user } = useAuth();
  const [branch, setBranch] = useState(null);
  const [kdsOn, setKdsOn] = useState(true);
  const [orders, setOrders] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [products, setProducts] = useState([]);
  const [sticker, setSticker] = useState(null);
  const [size, setSize] = useState("3x2");
  const prevAlerts = useRef(0);
  const prevOrderIds = useRef(null);

  const load = async () => {
    try {
      const [br, pr] = await Promise.all([api.get("/branches"), api.get("/products")]);
      const b = br.data.find((x) => x.id === user.branch_id) || br.data[0];
      setBranch(b); setKdsOn(b?.kds_enabled ?? true);
      setProducts(pr.data.filter((p) => !b || p.branch_id === b.id));
    } catch (e) {}
  };
  const poll = async () => {
    try {
      const bid = user.branch_id || "";
      const [o, a] = await Promise.all([api.get("/kds/orders", { params: { branch_id: bid } }), api.get("/alerts", { params: { status: "open", branch_id: bid } })]);
      setOrders(o.data);
      const ids = new Set(o.data.map((x) => x.id));
      if (prevOrderIds.current !== null) {
        const fresh = o.data.filter((x) => !prevOrderIds.current.has(x.id));
        if (fresh.length) {
          const f = fresh[0];
          const where = f.mode === "dinein" ? `Meja ${f.table}` : "Take Away";
          toast.success(`🔔 Pesanan baru: ${where}${f.kind === "addition" ? " · TAMBAHAN" : ""}`);
          playBell();
        }
      }
      prevOrderIds.current = ids;
      if (a.data.length > prevAlerts.current) { toast.warning(`🔔 Notifikasi Re-stock baru: ${a.data[0]?.product_name}`); playBell(); }
      prevAlerts.current = a.data.length;
      setAlerts(a.data);
    } catch (e) {}
  };
  useEffect(() => { load(); poll(); const t = setInterval(poll, 5000); return () => clearInterval(t); }, []);

  const toggleKds = async (v) => {
    setKdsOn(v);
    try { await api.put(`/branches/${branch.id}/settings`, { kds_enabled: v }); toast.success(`KDS ${v ? "diaktifkan" : "dinonaktifkan"}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const advance = async (o) => {
    const next = STATUS[o.status].next;
    try { await api.put(`/kds/orders/${o.id}/status`, { status: next }); poll(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resolveAlert = async (id) => { try { await api.put(`/alerts/${id}/resolve`); poll(); toast.success("Re-stock ditandai selesai"); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="min-h-screen bg-hotpot-grid pb-10">
      <TopBar>
        <div className="flex items-center gap-2 rounded-xl bg-neutral-100 px-3 py-1.5">
          <ChefHat size={15} className="text-crimson" />
          <span className="text-xs font-semibold text-neutral-900">KDS</span>
          <Switch data-testid="kds-toggle" checked={kdsOn} onCheckedChange={toggleKds} />
        </div>
      </TopBar>

      <div className="mx-auto max-w-[1600px] px-4 pt-6 sm:px-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-neutral-900 sm:text-3xl">Kitchen Display System</h1>
            <p className="text-sm text-muted-foreground">Pesanan masuk real-time & notifikasi re-stock kasir.</p>
          </div>
          <Button data-testid="open-sticker-btn" onClick={() => setSticker(products[0] || {})} variant="outline" className="border-neutral-200 font-bold"><BarcodeIcon size={16} className="mr-1" /> Cetak Stiker Barcode</Button>
        </div>

        {/* Re-stock alerts */}
        {alerts.length > 0 && (
          <Card className="card-surface mb-5 rounded-2xl border-amber-500/40 p-4" data-testid="restock-alerts">
            <div className="mb-2 flex items-center gap-2 font-display font-bold text-amber-400"><Bell size={16} /> Notifikasi Re-stock ({alerts.length})</div>
            <div className="flex flex-wrap gap-2">
              {alerts.map((a) => (
                <div key={a.id} className="flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2">
                  <span className="text-sm font-semibold text-neutral-900">{a.product_name}</span>
                  <span className="text-[10px] text-muted-foreground">oleh {a.created_by}</span>
                  <button data-testid={`resolve-alert-${a.id}`} onClick={() => resolveAlert(a.id)} className="rounded-lg bg-emerald-500/20 px-2 py-1 text-[10px] font-bold text-emerald-400">Selesai</button>
                </div>
              ))}
            </div>
          </Card>
        )}

        {!kdsOn ? (
          <Card className="card-surface flex flex-col items-center rounded-2xl p-16 text-center" data-testid="kds-disabled">
            <MonitorX size={48} className="mb-3 text-muted-foreground" />
            <p className="font-display text-lg font-bold text-neutral-900">KDS Dinonaktifkan</p>
            <p className="text-sm text-muted-foreground">Aktifkan toggle KDS di header untuk menampilkan pesanan dapur.</p>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="kds-orders">
            <AnimatePresence>
              {orders.map((o) => {
                const st = STATUS[o.status] || STATUS.pending;
                return (
                  <motion.div key={o.id} layout initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}>
                    <Card className="card-surface overflow-hidden rounded-2xl">
                      <div className={`flex items-center justify-between px-4 py-2 ${o.kind === "addition" ? "bg-amber-600" : st.color}`}>
                        <span className="font-display font-bold text-neutral-900">{o.mode === "dinein" ? `Meja ${o.table}` : "Take Away"}{o.kind === "addition" && <span className="ml-2 rounded-full bg-neutral-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-200">Tambahan</span>}</span>
                        <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold uppercase text-neutral-900">{st.label}</span>
                      </div>
                      <div className="p-4">
                        <div className="mb-2 font-mono-num text-[11px] text-muted-foreground">{o.order_no}</div>
                        <div className="space-y-1.5">
                          {o.items.map((i, k) => (
                            <div key={k} className="text-sm text-neutral-900"><span className="font-bold text-gold">{i.qty}x</span> {i.name}{i.note && <span className="block text-[11px] italic text-muted-foreground">“{i.note}”</span>}</div>
                          ))}
                        </div>
                        <Button data-testid={`kds-status-toggle-order-${o.id}`} onClick={() => advance(o)} className={`mt-4 touch-btn w-full font-bold text-white ${o.status === "ready" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-crimson hover:bg-red-500"}`}>
                          {o.status === "ready" ? <><CheckCircle2 size={16} className="mr-1" /> Selesai</> : <><Flame size={16} className="mr-1" /> {st.label} → {STATUS[st.next]?.label || "Selesai"}</>}
                        </Button>
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
            {orders.length === 0 && <div className="col-span-full py-16 text-center text-muted-foreground">Belum ada pesanan masuk. Pesanan Dine-In dari Kasir akan muncul di sini.</div>}
          </div>
        )}
      </div>

      {/* Sticker generator */}
      <Dialog open={!!sticker} onOpenChange={(o) => !o && setSticker(null)}>
        <DialogContent className="glass border-neutral-200 text-neutral-900">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><BarcodeIcon className="text-crimson" size={18} /> Cetak Stiker Barcode</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            {products.length > 0 && (
              <select data-testid="sticker-product" value={sticker?.id || ""} onChange={(e) => setSticker(products.find((p) => p.id === e.target.value))}
                className="touch-btn flex-1 rounded-xl bg-neutral-100 border border-neutral-200 px-3 text-sm text-neutral-900">
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
            <div className="flex gap-1 rounded-xl bg-neutral-100 p-1">
              {["3x2", "4x2"].map((s) => <button key={s} data-testid={`sticker-size-${s}`} onClick={() => setSize(s)} className={`rounded-lg px-3 text-xs font-bold ${size === s ? "bg-crimson text-white" : "text-muted-foreground"}`}>{s} cm</button>)}
            </div>
          </div>
          {sticker?.name && (
            <div data-testid="sticker-preview" className="mx-auto flex flex-col items-center justify-center rounded bg-white p-3 text-ebony" style={{ width: size === "3x2" ? 210 : 280, height: 150 }}>
              <div className="w-full truncate text-center font-display text-sm font-extrabold">{sticker.name}</div>
              <div className="font-mono-num text-[10px]">Kemas: {new Date().toLocaleDateString("id-ID")}</div>
              <div className="font-mono-num text-xs font-bold">Rp {Number(sticker.price).toLocaleString("id-ID")}</div>
              <Barcode value={sticker.barcode} height={52} width={size === "3x2" ? 2.1 : 2.6} className="mt-1 w-full" />
            </div>
          )}
          <Button data-testid="sticker-print-btn" onClick={() => sticker?.name && printSticker(sticker, size)} className="touch-btn w-full bg-crimson font-bold hover:bg-red-500">Cetak Stiker (Direct Thermal)</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
